import React, { useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useContactModal } from '../context/ContactModalContext';
import { t } from '../data/translations';

const CTASection = () => {
  const { language } = useLanguage();
  const { openContactModal } = useContactModal();
  const videoRef = useRef(null);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.play().catch(() => {
        console.log('Video autoplay prevented');
      });
    }
  }, []);

  return (
    <section 
      className="relative overflow-hidden"
      style={{ backgroundColor: '#E8A238' }}
    >
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid md:grid-cols-2 gap-8 items-center min-h-[500px]">
          {/* 3D Character Video */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="flex justify-center items-center order-2 md:order-1"
          >
            <div 
              className="w-[350px] h-[450px] md:w-[420px] md:h-[520px] relative"
              style={{ clipPath: 'inset(3% 5% 0% 5%)' }}
            >
              <video
                ref={videoRef}
                src="https://framerusercontent.com/assets/QvlrKImribrkeydweJGoTXNs.mp4"
                autoPlay
                loop
                muted
                playsInline
                preload="auto"
                className="w-full h-full object-contain"
              />
            </div>
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-center md:text-left order-1 md:order-2"
          >
            <h2 className="text-5xl md:text-6xl lg:text-7xl font-bold text-[#131132] leading-[0.95] mb-4">
              {t('cta.title1', language)}
            </h2>
            <h2 className="text-5xl md:text-6xl lg:text-7xl font-bold text-[#131132] leading-[0.95] mb-10">
              {t('cta.title2', language)}
            </h2>
            <p className="text-xl text-[#131132]/70 mb-10 max-w-lg">
              {t('cta.description', language)}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-5">
              <motion.button
                onClick={openContactModal}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="inline-flex items-center justify-center gap-3 px-8 py-5 bg-[#131132] text-white font-semibold text-lg rounded-xl hover:bg-black transition-colors group"
                data-testid="cta-contact-button"
              >
                {t('cta.button', language)}
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </motion.button>
              <motion.a
                href="tel:+421949303240"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="inline-flex items-center justify-center gap-3 px-8 py-5 border-2 border-[#131132] text-[#131132] font-semibold text-lg rounded-xl hover:bg-[#131132] hover:text-white transition-all group"
              >
                +421 949 303 240
              </motion.a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default CTASection;
